/**
 * `?` shortcuts cheat sheet. Lists the global keybindings the omnibar
 * advertises (⌘K, /, ?) plus the contextual shortcuts the user is most
 * likely to forget. Render as a sibling of CommandPalette in App.tsx;
 * close on Escape or backdrop click.
 *
 * Keep the list curated. A "comprehensive" shortcut overlay is a lie —
 * users want the 10 they actually use, not the full reference.
 */
import { type Component, For, Show, onCleanup, onMount } from "solid-js";

import { Kbd } from "@/ui/primitives";

interface ShortcutGroup {
  label: string;
  rows: Array<{ keys: string[]; desc: string }>;
}

const GROUPS: ShortcutGroup[] = [
  {
    label: "global",
    rows: [
      { keys: ["⌘", "K"], desc: "open command palette" },
      { keys: ["/"], desc: "open command palette (typing-aware)" },
      { keys: ["?"], desc: "open this cheat sheet" },
      { keys: ["esc"], desc: "close any overlay" },
    ],
  },
  {
    label: "chat",
    rows: [
      { keys: ["↵"], desc: "send message" },
      { keys: ["⇧", "↵"], desc: "newline in composer" },
      { keys: ["⌥ / Alt"], desc: "hold to dictate (voice)" },
    ],
  },
  {
    label: "palette",
    rows: [
      { keys: ["↑", "↓"], desc: "navigate results" },
      { keys: ["↵"], desc: "execute highlighted command" },
    ],
  },
];

interface Props {
  open: boolean;
  onClose: () => void;
}

const ShortcutsOverlay: Component<Props> = (props) => {
  onMount(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!props.open) return;
      if (e.key === "Escape") {
        e.preventDefault();
        props.onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    onCleanup(() => window.removeEventListener("keydown", onKey));
  });

  return (
    <Show when={props.open}>
      <div
        class="cmd-palette-backdrop"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) props.onClose();
        }}
      >
        <section class="shortcuts-overlay" aria-label="keyboard shortcuts">
          <header class="shortcuts-header">
            <h2>keyboard shortcuts</h2>
            <span>press esc to close</span>
          </header>
          <div class="shortcuts-grid">
            <For each={GROUPS}>
              {(group) => (
                <div class="shortcuts-group">
                  <h3>{group.label}</h3>
                  <ul>
                    <For each={group.rows}>
                      {(row) => (
                        <li>
                          <span class="shortcuts-keys">
                            <For each={row.keys}>
                              {(k, i) => (
                                <>
                                  <Kbd>{k}</Kbd>
                                  <Show when={i() < row.keys.length - 1}>
                                    <span class="shortcuts-plus">+</span>
                                  </Show>
                                </>
                              )}
                            </For>
                          </span>
                          <span class="shortcuts-desc">{row.desc}</span>
                        </li>
                      )}
                    </For>
                  </ul>
                </div>
              )}
            </For>
          </div>
        </section>
      </div>
    </Show>
  );
};

export default ShortcutsOverlay;
